import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  approvePayrollRun,
  createPayrollRun,
  fetchFinancialPeriods,
  fetchPayrollRuns,
  fetchPayrollStaff,
  postPayrollRun,
} from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatDate, formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { todayIsoDate, useLatestRef } from "./accountingUtils";

const calcNetPay = (gross, deductions) => {
  const grossNum = Number(gross) || 0;
  const deductionsNum = Number(deductions) || 0;
  const net = grossNum - deductionsNum;
  return net > 0 ? String(net) : "";
};

const staffMemberToLine = (member) => ({
  user_id: member.id,
  employee_name: member.full_name,
  gross_pay: member.suggested_gross_pay != null ? String(member.suggested_gross_pay) : "",
  deductions: member.suggested_deductions != null ? String(member.suggested_deductions) : "0",
  net_pay:
    member.suggested_net_pay != null
      ? String(member.suggested_net_pay)
      : calcNetPay(member.suggested_gross_pay, member.suggested_deductions),
});

const emptyLine = () => ({
  user_id: "",
  employee_name: "",
  gross_pay: "",
  deductions: "0",
  net_pay: "",
});

const PayrollPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState("");
  const [form, setForm] = useState({
    period_id: "",
    pay_date: todayIsoDate(),
    description: "Payroll",
    lines: [emptyLine()],
  });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  useEffect(() => {
    if (!branchId) return;
    fetchFinancialPeriods(branchId).then((p) => {
      setPeriods(p || []);
      const open = (p || []).find((x) => x.status === "open");
      if (open) setForm((prev) => ({ ...prev, period_id: open.id }));
    });
  }, [branchId]);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(() => fetchPayrollRuns(branchId).then(setRows), { errorMessage: "Failed to load payroll runs." })
      .catch((err) => setError(formatApiError(err, "Failed to load payroll runs.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const loadStaffForModal = useCallback(async () => {
    if (!branchId) return;
    setStaffLoading(true);
    setStaffError("");
    try {
      const members = await fetchPayrollStaff(branchId);
      setStaff(members || []);
      setForm((prev) => ({
        ...prev,
        lines: members?.length ? members.map(staffMemberToLine) : [emptyLine()],
      }));
    } catch (err) {
      setStaff([]);
      setStaffError(formatApiError(err, "Failed to load branch staff."));
    } finally {
      setStaffLoading(false);
    }
  }, [branchId]);

  const openNewRunModal = () => {
    setModalOpen(true);
    setStaffError("");
    loadStaffForModal();
  };

  const runAction = useCallback(
    (row, action) => {
      confirm({
        title: action === "approve" ? "Approve payroll?" : "Post payroll to GL?",
        confirmLabel: action === "approve" ? "Approve" : "Post",
        successMessage: action === "approve" ? "Payroll approved." : "Payroll posted.",
        onConfirm: async () => {
          if (action === "approve") await approvePayrollRun(row.id);
          else await postPayrollRun(row.id);
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Pay date", cell: (r) => formatDate(r.pay_date), width: "110px" },
      { name: "Description", selector: (r) => r.description, grow: 2 },
      { name: "Gross", cell: (r) => formatTZS(r.gross_total), right: true, width: "120px" },
      { name: "Net", cell: (r) => formatTZS(r.net_total), right: true, width: "120px" },
      {
        name: "Status",
        cell: (r) => {
          const colors = { draft: "secondary", approved: "primary", posted: "success" };
          return <Badge color={colors[r.status] || "secondary"}>{r.status}</Badge>;
        },
        width: "100px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              r.status === "draft" && {
                icon: "check",
                label: "Approve payroll",
                onClick: () => runAction(r, "approve"),
                variant: "primary",
              },
              r.status === "approved" && {
                icon: "check",
                label: "Post to GL",
                onClick: () => runAction(r, "post"),
                variant: "success",
              },
            ].filter(Boolean)}
          />
        ),
        width: "88px",
        ignoreRowClick: true,
      },
    ],
    [runAction]
  );

  const updateLine = (idx, field, value) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      const current = { ...lines[idx], [field]: value };

      if (field === "user_id" && value) {
        const member = staff.find((s) => String(s.id) === String(value));
        if (member) {
          Object.assign(current, staffMemberToLine(member));
        }
      }

      if (field === "gross_pay" || field === "deductions") {
        current.net_pay = calcNetPay(
          field === "gross_pay" ? value : current.gross_pay,
          field === "deductions" ? value : current.deductions
        );
      }

      lines[idx] = current;
      return { ...prev, lines };
    });
  };

  const addEmployeeLine = () => {
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const lines = form.lines
        .filter((l) => l.user_id && Number(l.net_pay) > 0)
        .map((l) => ({
          user_id: l.user_id,
          employee_name: l.employee_name,
          gross_pay: Number(l.gross_pay),
          deductions: Number(l.deductions) || 0,
          net_pay: Number(l.net_pay),
        }));
      if (!lines.length) {
        setStaffError("Add at least one staff member with gross pay and net pay greater than zero.");
        return;
      }
      await runCrud(
        () =>
          createPayrollRun({
            branch_id: branchId,
            period_id: form.period_id,
            pay_date: form.pay_date,
            description: form.description,
            lines,
          }),
        { successMessage: "Payroll run created." }
      );
      setModalOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const usedStaffIds = useMemo(
    () => new Set(form.lines.map((line) => String(line.user_id)).filter(Boolean)),
    [form.lines]
  );

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Payroll" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Payroll runs</h5>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Input
                type="select"
                bsSize="sm"
                style={{ width: "160px" }}
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Input>
              <Button color="primary" size="sm" onClick={openNewRunModal}>
                New payroll run
              </Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl">
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New payroll run</ModalHeader>
            <ModalBody>
              {staffError && <Alert color="danger">{staffError}</Alert>}
              <Row className="mb-3">
                <Col md="4">
                  <FormGroup>
                    <Label>Period</Label>
                    <Input
                      type="select"
                      required
                      value={form.period_id}
                      onChange={(e) => setForm({ ...form, period_id: e.target.value })}
                    >
                      {periods
                        .filter((p) => p.status === "open")
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Pay date</Label>
                    <Input
                      type="date"
                      required
                      value={form.pay_date}
                      onChange={(e) => setForm({ ...form, pay_date: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Description</Label>
                    <Input
                      required
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <strong>Staff</strong>
                  {staff.length > 0 && (
                    <span className="text-muted ms-2">{staff.length} active at this branch</span>
                  )}
                </div>
                <Button
                  size="sm"
                  color="light"
                  className="border"
                  type="button"
                  onClick={loadStaffForModal}
                  disabled={staffLoading || saving}
                >
                  {staffLoading ? "Loading staff…" : "Reload branch staff"}
                </Button>
              </div>
              {staffLoading ? (
                <div className="text-center py-4">
                  <Spinner size="sm" color="primary" /> Loading branch staff…
                </div>
              ) : staff.length === 0 ? (
                <Alert color="warning" className="mb-3">
                  No active staff are assigned to this branch. Assign users to the branch in User Management, then
                  reload staff.
                </Alert>
              ) : (
                <Table size="sm" bordered responsive>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Gross</th>
                      <th>Deductions</th>
                      <th>Net pay</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((line, idx) => (
                      <tr key={idx}>
                        <td>
                          <Input
                            bsSize="sm"
                            type="select"
                            value={line.user_id}
                            onChange={(e) => updateLine(idx, "user_id", e.target.value)}
                          >
                            <option value="">Select staff…</option>
                            {staff
                              .filter(
                                (member) =>
                                  String(member.id) === String(line.user_id) ||
                                  !usedStaffIds.has(String(member.id))
                              )
                              .map((member) => (
                                <option key={member.id} value={member.id}>
                                  {member.full_name}
                                  {member.department_name ? ` · ${member.department_name}` : ""}
                                </option>
                              ))}
                          </Input>
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.gross_pay}
                            onChange={(e) => updateLine(idx, "gross_pay", e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.deductions}
                            onChange={(e) => updateLine(idx, "deductions", e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.net_pay}
                            readOnly
                            className="bg-light"
                          />
                        </td>
                        <td>
                          {form.lines.length > 1 && (
                            <Button
                              size="sm"
                              color="link"
                              className="text-danger"
                              type="button"
                              onClick={() =>
                                setForm((p) => ({
                                  ...p,
                                  lines: p.lines.filter((_, i) => i !== idx),
                                }))
                              }
                            >
                              Remove
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {!staffLoading && staff.length > 0 && (
                <Button size="sm" color="light" className="border" type="button" onClick={addEmployeeLine}>
                  Add employee
                </Button>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving || staffLoading || !staff.length}>
                Save draft
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default PayrollPage;
